import React from "react";
import { ModalProps, RequiredKeys } from "@hex-insights/core";
import { FormType, useFormState } from "@hex-insights/forms";
import { useHistory } from "@hex-insights/router";
import {
	MessageFormValues,
	MessageGroupComponents,
	MessageGroupCreateMutation,
	useOrderedLabels,
	User,
	UserGroup,
} from "@s-i-s-i/see-learning.shared";
import { AuthenticationContext } from "../../../../Contexts";
import { messagingMessageGroupPageInfo } from "../../MessageGroup/pageinfo";

type NewMessageFormValues = Pick<MessageFormValues.Create, "body" | "mediaAttachments">;

const defaultInitialUserRecipientIDs: User["id"][] = [];
const defaultInitialUserGroupRecipientIDs: UserGroup["id"][] = [];

enum Page {
	Recipients,
	Message,
}

const pages = [Page.Recipients, Page.Message];

export type NewMessageModalProps = {
	initialUserRecipientIDs?: User["id"][];
	initialUserGroupRecipientIDs?: User["id"][];
} & RequiredKeys<Pick<ModalProps, "ifRef" | "onClose">, "onClose">;

export function NewMessageModal({
	initialUserRecipientIDs = defaultInitialUserRecipientIDs,
	initialUserGroupRecipientIDs = defaultInitialUserGroupRecipientIDs,
	ifRef,
	onClose,
}: NewMessageModalProps) {
	const { user } = React.useContext(AuthenticationContext);

	const [userRecipientIDs, setUserRecipientIDs] = React.useState<User["id"][]>(initialUserRecipientIDs);
	const [userGroupRecipientIDs, setUserGroupRecipientIDs] = React.useState<User["id"][]>(initialUserGroupRecipientIDs);

	const formState = useFormState<NewMessageFormValues>({
		initialFormValues: { body: "", mediaAttachments: [] },
		formType: FormType.Create,
	});

	const { push: pushHistory } = useHistory();
	const onSuccess = React.useCallback(
		(data?: MessageGroupCreateMutation["createMessageGroup"] | null) => {
			if (data) {
				pushHistory(messagingMessageGroupPageInfo.to(data.id));
			}
		},
		[pushHistory],
	);

	const { data: recipientData } = MessageGroupComponents.Create.InternalPortal.useRecipients();
	const { availableUserRecipients, availableUserGroupRecipients } = React.useMemo(
		() => ({
			availableUserRecipients: recipientData?.userConnection.edges.map((e) => e.node) ?? [],
			availableUserGroupRecipients: recipientData?.userGroupConnection.edges.map((e) => e.node) ?? [],
		}),
		[recipientData],
	);

	const contextValue = React.useMemo<MessageGroupComponents.Create.MessageGroupCreateContextValue>(
		() => ({
			selectedUserRecipients: availableUserRecipients.filter((e) => userRecipientIDs.includes(e.id)),
			selectedUserGroupRecipients: availableUserGroupRecipients.filter((e) => userGroupRecipientIDs.includes(e.id)),
		}),
		[availableUserRecipients, userRecipientIDs, availableUserGroupRecipients, userGroupRecipientIDs],
	);

	const {
		label: page,
		setLabel: setPage,
		labelIndex: pageIndex,
		incrementLabel: incrementPage,
		decrementLabel: decrementPage,
	} = useOrderedLabels(pages, () => {
		if (initialUserRecipientIDs.length > 0 || initialUserGroupRecipientIDs.length > 0) {
			return Page.Message;
		}
		return Page.Recipients;
	});

	const requiresRunRef = React.useRef(true);
	const hasUserGroupRecipients = userGroupRecipientIDs.length > 0;
	React.useLayoutEffect(() => {
		if (!requiresRunRef.current) {
			return;
		}
		if (!recipientData) {
			return;
		}
		requiresRunRef.current = false;
		if (pageIndex <= pages.indexOf(Page.Recipients)) {
			return;
		}

		setPage(Page.Message);
	}, [recipientData, pageIndex, hasUserGroupRecipients, setPage]);

	if (page === Page.Recipients) {
		return (
			<MessageGroupComponents.Create.MessageGroupCreateContext.Provider value={contextValue}>
				<MessageGroupComponents.Create.NewMessageRecipientsModal
					userRecipientIDs={userRecipientIDs}
					setUserRecipientIDs={setUserRecipientIDs}
					userGroupRecipientIDs={userGroupRecipientIDs}
					setUserGroupRecipientIDs={setUserGroupRecipientIDs}
					RecipientSections={RecipientSections}
					nextDisabled={!recipientData}
					onNextClick={incrementPage}
					onCancelClick={onClose}
					ifRef={ifRef}
					onClose={onClose}
				/>
			</MessageGroupComponents.Create.MessageGroupCreateContext.Provider>
		);
	}

	if (!recipientData) {
		return (
			<MessageGroupComponents.Create.MessageGroupCreateContext.Provider value={contextValue}>
				<MessageGroupComponents.Create.NewMessageLoadingModal ifRef={ifRef} onClose={onClose}>
					Loading...
				</MessageGroupComponents.Create.NewMessageLoadingModal>
			</MessageGroupComponents.Create.MessageGroupCreateContext.Provider>
		);
	}

	return (
		<MessageGroupComponents.Create.MessageGroupCreateContext.Provider value={contextValue}>
			<MessageGroupComponents.Create.NewMessageSubmittableMessageModal
				formState={formState}
				userID={user!.id}
				userRecipientIDs={userRecipientIDs}
				userGroupRecipientIDs={userGroupRecipientIDs}
				body={formState.formValues.body}
				mediaAttachments={formState.formValues.mediaAttachments}
				setUserRecipientIDs={setUserRecipientIDs}
				RecipientSections={MessageEditRecipientSections}
				onBackClick={decrementPage}
				ifRef={ifRef}
				onClose={onClose}
				onSuccess={onSuccess}
			/>
		</MessageGroupComponents.Create.MessageGroupCreateContext.Provider>
	);
}

function RecipientSections(props: MessageGroupComponents.Create.RecipientSectionsProps) {
	return <MessageGroupComponents.Create.InternalPortal.MessageRecipientSections {...props} withSelected />;
}

function MessageEditRecipientSections(props: MessageGroupComponents.Create.MessageEditRecipientSectionsProps) {
	return <MessageGroupComponents.Create.InternalPortal.MessageEditRecipientSections {...props} />;
}
